import { SHARE_CONTENT_CLASS_NAME } from "./productShareLayout";
import { ArrowRight } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { publicPortfolioFactorKey } from "../hooks/usePublicProductStats";
import type { FactorEntry } from "../services/productFactorsService";
import type { TrackingEventProps } from "../services/trackingEvents";
import type {
  ProductShareMetadata,
  PublicProductStats,
  PublicTopPortfolioStat,
} from "../services/shareableLinksService";
import { formatNumber, formatSignedPercentage } from "../utils/numberUtils";
import { buildSparklinePath } from "../pages/dashboard/components/StrategyPerformanceWidget";
import { productDetailPath } from "./ProductShareUnlockedHoldings";

const DISCOVER_PATH = "/alpha/discover";

/**
 * Where a conversion click goes. Signed-in viewers get a real route inside
 * the app; anonymous viewers open the sign-up modal on this page.
 */
export type DiscoverAction =
  | { kind: "link"; to: string }
  | { kind: "modal"; open: (ctaId: string) => void };

/**
 * Marks children to rise in once they scroll into view (V5D). Returns a
 * callback ref so the observer attaches whenever the element mounts, not only
 * on the hook's first effect: the cards grid mounts after the public stats
 * resolve, which on a live page is usually after the band itself.
 */
function useRevealOnScroll<T extends HTMLElement>(): [(node: T | null) => void, boolean] {
  const [isIn, setIsIn] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsIn(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsIn(true);
        observer.disconnect();
        observerRef.current = null;
      }
    }, { rootMargin: "0px 0px -8% 0px" });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return [ref, isIn];
}

function revealClassName(isIn: boolean, index = 0): string {
  return `transition-[opacity,transform] duration-[550ms] ease-out motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0 ${
    isIn ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
  } [transition-delay:${index * 70}ms]`;
}

function ActionWrapper({
  action,
  ctaId,
  onClick,
  className,
  ariaLabel,
  children,
}: {
  action: DiscoverAction;
  ctaId: string;
  onClick: () => void;
  className: string;
  ariaLabel?: string;
  children: React.ReactNode;
}) {
  if (action.kind === "link") {
    return (
      <Link to={action.to} onClick={onClick} className={className} aria-label={ariaLabel}>
        {children}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={() => { onClick(); action.open(ctaId); }}
      className={`${className} text-left`}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}

function DiscoverCard({
  portfolio,
  rank,
  providerName,
  isCurrent,
  factors,
  action,
  isIn,
  isSignedIn,
  onClick,
}: {
  portfolio: PublicTopPortfolioStat;
  rank: number;
  providerName: string;
  isCurrent: boolean;
  factors: FactorEntry[];
  action: DiscoverAction;
  isIn: boolean;
  isSignedIn: boolean;
  onClick: () => void;
}) {
  const positionCount = portfolio.numOfStocks + portfolio.numOfOptions;
  const ytdReturn = portfolio.performance.returnYtd;
  const sparklinePath = useMemo(() => buildSparklinePath(factors, 240, 44), [factors]);
  const isNegative = ytdReturn !== null && ytdReturn < 0;
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = portfolio.providerId !== null && !logoFailed;

  return (
    <ActionWrapper
      action={action}
      ctaId="discover_portfolio_tile"
      onClick={onClick}
      ariaLabel={isSignedIn ? `Open ${portfolio.title} on Plutus` : `Sign up to discover ${portfolio.title}`}
      className={`relative block w-full rounded-v7-panel border border-v7-ink/10 bg-v7-white p-[18px] shadow-sm transition-colors duration-300 hover:border-v7-bronze focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark ${revealClassName(isIn, rank - 1)}`}
    >
      <span className="absolute right-3.5 top-3 text-xs text-v7-ink/45">#{formatNumber(rank, 0)}</span>
      <div className="flex items-center gap-2 pr-8 text-[13px] text-v7-ink/60">
        {showLogo ? (
          <img
            src={`/provider-assets/${portfolio.providerId}/logo.png`}
            alt=""
            onError={() => setLogoFailed(true)}
            className="h-6 w-6 rounded-[5px] border border-v7-ink/15 object-cover"
          />
        ) : (
          <span className="flex h-6 w-6 items-center justify-center rounded-[5px] border border-v7-ink/15 bg-v7-paper font-v7-serif text-xs text-v7-ink">
            {(providerName.trim()[0] ?? "P").toUpperCase()}
          </span>
        )}
        <span className="truncate">{providerName}</span>
      </div>
      <div className="mt-3.5 flex flex-wrap items-center gap-2 text-[15px] font-semibold text-v7-ink">
        <span className="truncate">{portfolio.title}</span>
        {isCurrent && (
          <span className="rounded-v7-control border border-v7-bronze/35 bg-v7-bronze/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[1px] text-v7-bronze-dark">
            Viewing
          </span>
        )}
      </div>
      <div className={`mt-2.5 text-[28px] leading-8 tracking-[-0.5px] ${isNegative ? "text-v7-rust" : "text-v7-success"}`}>
        {ytdReturn === null ? "--" : formatSignedPercentage(ytdReturn)}
      </div>
      <p className="mt-0.5 text-xs text-v7-ink/60">
        YTD return - {formatNumber(positionCount, 0)} positions
      </p>
      <div className="mt-3.5 h-11 w-full" aria-label="Portfolio performance graph">
        {sparklinePath ? (
          <svg viewBox="0 0 240 44" preserveAspectRatio="none" className={`h-full w-full ${isNegative ? "text-v7-rust" : "text-v7-success"}`} role="img">
            <path d={sparklinePath} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </svg>
        ) : (
          <div className="h-full w-full rounded-v7-control bg-v7-mist" />
        )}
      </div>
    </ActionWrapper>
  );
}

export interface ProductShareDiscoverBandProps {
  metadata: ProductShareMetadata | null;
  stats: PublicProductStats | null;
  providerNames: Record<number, string>;
  portfolioFactors: Record<string, FactorEntry[]>;
  isLoading: boolean;
  error: string | null;
  isSignedIn: boolean;
  openGateModal: ((ctaId: string) => void) | null;
  signupPath: string;
  onSignupClick: (ctaId: string, details?: TrackingEventProps) => void;
}

/**
 * V5D Discover: a full-width band one tone deeper than the canvas, four
 * provider cards that rise in as they enter view, then a dark bookend with
 * Browse All. Everything here opens the sign-up modal for anonymous viewers.
 */
const ProductShareDiscoverBand: React.FC<ProductShareDiscoverBandProps> = ({
  metadata,
  stats,
  providerNames,
  portfolioFactors,
  isLoading,
  error,
  isSignedIn,
  openGateModal,
  signupPath,
  onSignupClick,
}) => {
  const [cardsRef, cardsIn] = useRevealOnScroll<HTMLDivElement>();
  const [askRef, askIn] = useRevealOnScroll<HTMLDivElement>();
  const moreCount = (stats?.numOfPortfolios ?? 0) + (stats?.numOfBaskets ?? 0);

  const actionFor = (to: string): DiscoverAction => {
    if (isSignedIn) return { kind: "link", to };
    if (openGateModal) return { kind: "modal", open: openGateModal };
    return { kind: "link", to: signupPath };
  };

  return (
    <section
      id="product-share-v2-gallery"
      aria-labelledby="product-share-discover-heading"
      className="relative left-1/2 w-screen -translate-x-1/2 bg-v7-sand pt-8 pb-[72px]"
    >
      <div className={SHARE_CONTENT_CLASS_NAME}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-1">
          <h2 id="product-share-discover-heading" className="text-[34px] font-light leading-tight tracking-[-0.6px] text-v7-ink">Discover</h2>
          {moreCount > 0 && (
            <span className="text-[13px] text-v7-ink/60">
              {formatNumber(moreCount, 0)} more portfolios and baskets on Plutus
            </span>
          )}
        </div>

        {isLoading && (
          <div className="mt-[18px] grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-44 animate-pulse rounded-v7-panel border border-v7-ink/10 bg-v7-white" />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div className="mt-[18px] rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-4 text-sm text-v7-rust">{error}</div>
        )}

        {!isLoading && stats && (
          <div ref={cardsRef} className="mt-[18px] grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {stats.topPortfolios.slice(0, 4).map((portfolio, index) => {
              const isCurrent = metadata?.productType === "PORTFOLIO"
                && portfolio.productId === metadata.productId
                && portfolio.providerId === metadata.providerId;
              const providerName = isCurrent && metadata
                ? metadata.providerName
                : portfolio.providerId === null
                  ? "Provider on Plutus"
                  : providerNames[portfolio.providerId] ?? "Provider on Plutus";
              const appPath = portfolio.providerId === null
                ? DISCOVER_PATH
                : productDetailPath("PORTFOLIO", portfolio.providerId, portfolio.productId);
              return (
                <DiscoverCard
                  key={`${portfolio.providerId ?? "none"}-${portfolio.productId}`}
                  portfolio={portfolio}
                  rank={index + 1}
                  providerName={providerName}
                  isCurrent={isCurrent}
                  factors={portfolio.providerId === null
                    ? []
                    : portfolioFactors[publicPortfolioFactorKey(portfolio.providerId, portfolio.productId)] ?? []}
                  action={actionFor(appPath)}
                  isIn={cardsIn}
                  isSignedIn={isSignedIn}
                  onClick={() => onSignupClick("discover_portfolio_tile", {
                    target_product_id: portfolio.productId,
                    target_provider_id: portfolio.providerId,
                    target_rank: index + 1,
                    target_is_current: isCurrent,
                  })}
                />
              );
            })}
          </div>
        )}

        <div
          ref={askRef}
          className={`mt-4 flex flex-col gap-4 rounded-v7-panel bg-v7-ink p-[18px] text-v7-white sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:py-[30px] ${revealClassName(askIn, 4)}`}
        >
          <h3 className="text-[26px] font-light leading-tight tracking-[-0.6px]">
            Not this one?{" "}
            <span className="text-v7-white/60">
              {moreCount > 0 ? `There are ${formatNumber(moreCount, 0)} more.` : "There are more."}
            </span>
          </h3>
          <ActionWrapper
            action={actionFor(DISCOVER_PATH)}
            ctaId="discover_all_portfolios"
            onClick={() => onSignupClick("discover_all_portfolios")}
            className="inline-flex min-h-12 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-v7-control border border-v7-white bg-v7-white px-5 text-sm font-medium text-v7-ink transition-colors hover:bg-v7-canvas focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze sm:w-auto"
          >
            Browse All
            <ArrowRight className="h-3.5 w-3.5 text-v7-bronze" aria-hidden="true" />
          </ActionWrapper>
        </div>
      </div>
    </section>
  );
};

export default ProductShareDiscoverBand;
